import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building2, FolderKanban, TrendingUp, LogOut, Menu, Users, Shield, ShieldCheck, UserCheck, Briefcase, ChevronDown, FileText, LayoutDashboard, Receipt, ArrowRightLeft, ClipboardCheck, ClipboardList, FolderLock, Mail, Send, MessageCircle, Layers } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { complianceAPI } from '../../services/api';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, switchAMC } = useAuth();
  const [amcInfo, setAmcInfo] = useState(null);
  const [investorsOpen, setInvestorsOpen] = useState(false);
  const [showAmcSwitcher, setShowAmcSwitcher] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [reassignBadge, setReassignBadge] = useState(0);

  const ep = user?.effective_permissions || user?.module_permissions || {};

  useEffect(() => {
    if (user && user.amc_id && user.user_type === 'amc_user') {
      fetchAMCInfo();
    }
  }, [user]);

  // Poll pending reassignment count so the sidebar badge stays fresh
  // even when the user is on other pages. 60s poll, plus a refresh on
  // route change so accepting/declining updates the count immediately.
  useEffect(() => {
    if (!user || user.user_type !== 'amc_user' || ep.compliance !== true) {
      setReassignBadge(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await complianceAPI.pendingReassignmentCount();
        if (!cancelled) setReassignBadge(res.data?.count || 0);
      } catch (err) {
        // Silent — permissions or offline; leave the previous count alone.
      }
    };
    load();
    const timer = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [user, ep.compliance, location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith('/investors')) setInvestorsOpen(true);
  }, [location.pathname]);

  const fetchAMCInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/amcs/me`);
      setAmcInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch AMC info:', error);
      setAmcInfo({ name: 'Your AMC' });
    }
  };

  const handleSwitchAMC = async (amcId) => {
    if (amcId === user?.amc_id && amcId !== '__owner__') {
      setShowAmcSwitcher(false);
      return;
    }
    setSwitching(true);
    try {
      await switchAMC(amcId);
      setShowAmcSwitcher(false);
      navigate(amcId === '__owner__' ? '/owner/dashboard' : '/funds');
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to switch');
    } finally {
      setSwitching(false);
    }
  };

  const baseNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permKey: 'dashboard' },
    { name: 'Funds', href: '/funds', icon: FolderKanban, permKey: 'funds' },
    { name: 'Investments', href: '/investments', icon: TrendingUp, permKey: 'investments' },
    { name: 'Drawdowns', href: '/drawdowns', icon: Receipt, permKey: 'drawdowns' },
    { name: 'AI Certification', href: '/ai-certification', icon: ClipboardCheck, permKey: 'ai_certification' },
    { name: 'Data Rooms', href: '/data-rooms', icon: FolderLock, permKey: 'data_rooms' },
    { name: 'Compliance Tracker', href: '/compliance', icon: ClipboardList, permKey: 'compliance' },
  ];

  const investorsChildren = [
    { name: 'Contacts', href: '/investors/contacts', icon: UserCheck, permKey: 'investor_contacts' },
    { name: 'Accounts', href: '/investors/accounts', icon: Briefcase, permKey: 'investor_accounts' },
    { name: 'Reports', href: '/investors/reports', icon: FileText, permKey: 'investor_reports' },
    { name: 'Communication', href: '/investors/communication', icon: Mail, permKey: 'investor_accounts' },
    { name: 'Transfer Commitment', href: '/investors/transfers', icon: ArrowRightLeft, permKey: 'investor_accounts' },
    { name: 'Dematerialisation', href: '/investors/dematerialisation', icon: Layers, permKey: 'investor_accounts' },
  ];

  const navigation = baseNavigation.filter(item => ep[item.permKey] === true);
  const showInvestors = ep.investors === true;
  const visibleInvestorChildren = investorsChildren.filter(c => ep[c.permKey] === true);

  const adminNavigation = user?.role === 'admin'
    ? [
        { name: 'User Management', href: '/users', icon: Users, adminOnly: true },
        { name: 'Compliance Recipients', href: '/compliance-recipients', icon: ShieldCheck, adminOnly: true },
        { name: 'Audit Trail', href: '/audit-trail', icon: Shield, adminOnly: true },
      ]
    : [];

  // Per-user settings (visible to every AMC user). Email + WhatsApp items
  // appear only when the AMC Admin has granted the corresponding sending
  // permission — so a viewer who can only browse data doesn't see config
  // pages they have no use for. Admins always see everything so they can
  // configure on behalf of teammates if needed.
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const canSendEmail = isAdmin || !!user?.email_sending_enabled;
  const canSendWhatsApp = isAdmin || !!user?.whatsapp_sending_enabled;
  const userSettingsNavigation = user?.user_type === 'amc_user'
    ? [
        ...(canSendEmail ? [
          { name: 'Email Configuration', href: '/email-config', icon: Mail },
          { name: 'Email Templates', href: '/email-templates', icon: FileText },
        ] : []),
        ...(canSendWhatsApp ? [
          { name: 'WhatsApp Configuration', href: '/whatsapp-config', icon: MessageCircle },
          { name: 'WhatsApp Templates', href: '/whatsapp-templates', icon: MessageCircle },
        ] : []),
        ...(canSendEmail ? [
          { name: 'Sent Emails', href: '/sent-emails', icon: Send },
        ] : []),
      ]
    : [];

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-blue-600 text-white rounded-md"
        data-testid="mobile-menu-toggle"
      >
        <Menu size={24} />
      </button>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-64 bg-gradient-to-b from-blue-900 to-blue-800 text-white
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        <div className="h-full flex flex-col">
          {/* Logo & AMC Info */}
          <div className="p-6 border-b border-blue-700">
            <h1 className="text-2xl font-bold">PrimeAIF Fund Manager</h1>
            {user?.user_type === 'amc_user' && user?.amc_id && (
              <div className="mt-3 relative">
                <div
                  className="px-3 py-2 bg-blue-800 bg-opacity-50 rounded-lg border border-blue-600 cursor-pointer"
                  onClick={() => (user?.amc_memberships?.length > 1 || user?.has_owner_portal) && setShowAmcSwitcher(!showAmcSwitcher)}
                  data-testid="amc-info-block"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-blue-300 mb-1">
                      <Building2 size={12} className="mr-1" />
                      <span>AMC</span>
                    </div>
                    {(user?.amc_memberships?.length > 1 || user?.has_owner_portal) && (
                      <ArrowRightLeft size={12} className="text-blue-300" />
                    )}
                  </div>
                  <p className="text-sm text-white font-medium truncate">
                    {amcInfo ? amcInfo.name : 'Loading...'}
                  </p>
                </div>
                {/* AMC + Owner Portal Switcher Dropdown */}
                {showAmcSwitcher && (user?.amc_memberships?.length > 1 || user?.has_owner_portal) && (
                  <div className="absolute left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border z-50 overflow-hidden" data-testid="amc-switcher-dropdown">
                    <div className="px-3 py-2 bg-gray-50 border-b">
                      <span className="text-xs font-medium text-gray-500">Switch workspace</span>
                    </div>
                    {user?.has_owner_portal && (
                      <button
                        onClick={() => handleSwitchAMC('__owner__')}
                        disabled={switching}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-purple-50 border-b bg-purple-50/40 transition-colors flex items-center gap-2 text-purple-800 font-medium"
                        data-testid="switch-to-owner-portal"
                      >
                        <Shield size={14} className="text-purple-500" />
                        <div>
                          <div>Owner Portal</div>
                          <div className="text-xs text-purple-400">Platform management</div>
                        </div>
                      </button>
                    )}
                    {(user.amc_memberships || []).map((m) => (
                      <button
                        key={m.amc_id}
                        onClick={() => handleSwitchAMC(m.amc_id)}
                        disabled={switching}
                        className={`w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 transition-colors flex items-center gap-2 ${
                          m.amc_id === user.amc_id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                        data-testid={`switch-amc-${m.amc_id}`}
                      >
                        <Building2 size={14} className={m.amc_id === user.amc_id ? 'text-blue-500' : 'text-gray-400'} />
                        <div>
                          <div>{m.amc_name}</div>
                          <div className="text-xs text-gray-400 capitalize">{m.role}</div>
                        </div>
                        {m.amc_id === user.amc_id && <span className="ml-auto text-xs text-blue-500">Current</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="mt-3">
              <p className="text-sm text-blue-200">{user?.name}</p>
              <p className="text-xs text-blue-300 capitalize">{user?.role}</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const showBadge = item.href === '/compliance' && reassignBadge > 0;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                    isActive(item.href) ? 'bg-blue-700 text-white' : 'text-blue-100 hover:bg-blue-800'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setIsOpen(false)}
                >
                  <Icon size={20} />
                  <span className="font-medium flex-1">{item.name}</span>
                  {showBadge && (
                    <span
                      className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center animate-pulse"
                      data-testid="nav-compliance-badge"
                      title={`${reassignBadge} pending reassignment request${reassignBadge > 1 ? 's' : ''}`}
                    >
                      {reassignBadge > 99 ? '99+' : reassignBadge}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* Investors expandable section */}
            {showInvestors && visibleInvestorChildren.length > 0 && (
              <div>
                <button
                  onClick={() => setInvestorsOpen(!investorsOpen)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors duration-200 ${
                    location.pathname.startsWith('/investors') ? 'bg-blue-700 text-white' : 'text-blue-100 hover:bg-blue-800'
                  }`}
                  data-testid="nav-investors"
                >
                  <div className="flex items-center space-x-3">
                    <UserCheck size={20} />
                    <span className="font-medium">Investors</span>
                  </div>
                  <ChevronDown size={16} className={`transition-transform ${investorsOpen ? 'rotate-180' : ''}`} />
                </button>
                {investorsOpen && (
                  <div className="ml-4 mt-1 space-y-1">
                    {visibleInvestorChildren.map(child => {
                      const ChildIcon = child.icon;
                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg text-sm transition-colors duration-200 ${
                            isActive(child.href) ? 'bg-blue-700/80 text-white' : 'text-blue-200 hover:bg-blue-800/60'
                          }`}
                          data-testid={`nav-${child.name.toLowerCase()}`}
                          onClick={() => setIsOpen(false)}
                        >
                          <ChildIcon size={16} />
                          <span>{child.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Admin nav */}
            {adminNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors duration-200 border-l-4 border-yellow-400 ${
                    isActive(item.href) ? 'bg-blue-700 text-white' : 'text-blue-100 hover:bg-blue-800'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  onClick={() => setIsOpen(false)}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.name}</span>
                  <Shield size={14} className="ml-auto text-yellow-400" />
                </Link>
              );
            })}

            {/* Per-user Settings */}
            {userSettingsNavigation.length > 0 && (
              <div className="pt-3 mt-3 border-t border-blue-700/40">
                <p className="px-4 mb-1 text-[10px] uppercase tracking-wider text-blue-300/70 font-semibold">My Settings</p>
                {userSettingsNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                        isActive(item.href) ? 'bg-blue-700 text-white' : 'text-blue-100 hover:bg-blue-800'
                      }`}
                      data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                      onClick={() => setIsOpen(false)}
                    >
                      <Icon size={18} />
                      <span className="text-sm font-medium">{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-blue-700">
            <button
              onClick={logout}
              className="flex items-center space-x-3 px-4 py-3 rounded-lg w-full
                text-blue-100 hover:bg-blue-800 transition-colors duration-200"
              data-testid="logout-button"
            >
              <LogOut size={20} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
